// API functions for charity validation
const serverURL = import.meta.env.VITE_API_URL

/**
 * Interface for postal address information from the NZ Charities Register API
 */
export interface CharityPostalAddress {
  address1: string
  address2?: string
  city?: string
  suburb?: string
  postal_code?: string
}

/**
 * Interface for charity details returned from the NZ Charities Register API
 * This matches the structure defined in the backend's charity_models.py
 */
export interface CharityDetails {
  name: string
  registration_number: string
  registration_status: string
  charity_type?: string | null
  purposes?: string | null
  registration_date?: string | null
  phone?: string | null
  email?: string | null
  website?: string | null
  postal_address?: CharityPostalAddress | null
  reporting_tier?: number | null
}

/**
 * Validates a charity number against the NZ Charities Register
 * @param token Auth token
 * @param charityNumber The charity number to validate (e.g., CC12345)
 * @returns Charity details or error
 */
export async function validateCharity(
  token: string,
  charityNumber: string
): Promise<{ success: boolean; data?: CharityDetails; error?: string }> {
  try {
    // Ensure charity number is properly formatted
    if (!charityNumber || !charityNumber.match(/^CC\d+$/)) {
      return {
        success: false,
        error:
          'Invalid charity number format. Should be CC followed by numbers.',
      }
    }

    // The API URL should include the /v1 prefix as configured in the backend
    const response = await fetch(
      `${serverURL}/charities/validate?number=${encodeURIComponent(
        charityNumber
      )}`,
      {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      }
    )

    if (!response.ok) {
      // Handle 404 (charity not found) specifically
      if (response.status === 404) {
        return {
          success: false,
          error: 'Charity not found in the registry',
        }
      }

      try {
        const errorData = await response.json()
        return {
          success: false,
          error:
            errorData.detail ||
            `API error: ${response.status} ${response.statusText}`,
        }
      } catch {
        return {
          success: false,
          error: `API error: ${response.status} ${response.statusText}`,
        }
      }
    }

    const rawData = await response.json()

    // The charity details are returned directly from our API
    // Our backend API already processed the OData format response
    if (rawData) {
      try {
        // Validate the response contains the required fields
        if (
          typeof rawData === 'object' &&
          rawData !== null &&
          'name' in rawData &&
          'registration_number' in rawData &&
          'registration_status' in rawData
        ) {
          // Map to CharityDetails interface
          const charityDetails: CharityDetails = {
            name: rawData.name,
            registration_number: rawData.registration_number,
            registration_status: rawData.registration_status,
            charity_type: rawData.charity_type || null,
            purposes: rawData.purposes || null,
            registration_date: rawData.registration_date || null,
            phone: rawData.phone || null,
            email: rawData.email || null,
            website: rawData.website || null,
            postal_address: rawData.postal_address || null,
            reporting_tier: rawData.reporting_tier || null,
          }

          return { success: true, data: charityDetails }
        } else {
          return {
            success: false,
            error: 'Invalid data format received from API',
          }
        }
      } catch {
        return { success: false, error: 'Error processing charity data' }
      }
    } else {
      return { success: false, error: 'Charity not found in the registry' }
    }
  } catch {
    return {
      success: false,
      error: 'Network error when validating charity number',
    }
  }
}
